import { ComponentProps, ReactNode, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SearchableTemplateList, TemplateList, TemplateListItem } from './template-list';
import { WEXLER_PROMPT_TEMPLATES } from './wexler-prompt-templates';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import { PromptTemplateScope } from '../../../../../../../../../../backend/src/prompt-template/dto/prompt-template.constants';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeletePromptTemplate } from '@/api/mutations/useDeletePromptTemplate';
import { useGetPromptTemplates } from '@/api/queries/useGetPromptTemplates';
import { useHasPermission } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

interface TemplatesPopoverProps {
  onSelect: (body: string) => void;
  onOpenTemplateBuilder: () => void;
  onEditTemplate: (template: { id: string; matterId?: string; title: string; body: string }) => void;
  /**
   * Controlled open state. When provided (together with a `PopoverAnchor` via `anchor`), the
   * built-in "Templates" trigger button is not rendered — used to drive the popover from an
   * external menu item (e.g. the composer's actions menu).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchor?: ReactNode;
  side?: ComponentProps<typeof PopoverContent>['side'];
  align?: ComponentProps<typeof PopoverContent>['align'];
}

export function TemplatesPopover({
  onSelect,
  onOpenTemplateBuilder,
  onEditTemplate,
  open,
  onOpenChange,
  anchor,
  side,
  align = 'end',
}: TemplatesPopoverProps) {
  const isControlled = open !== undefined;
  const { t } = useTranslation();
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const canManageOrgTemplates = hasPermission(Permission.PromptTemplateManage);
  const { data: userTemplates = [] } = useGetPromptTemplates();
  const { data: orgTemplates = [] } = useGetPromptTemplates({ scope: PromptTemplateScope.Organisation });
  const { mutate: deleteTemplate, isLoading: isDeleting } = useDeletePromptTemplate(matterId);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateListItem | null>(null);
  const [showOtherMatterTemplates, setShowOtherMatterTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  const wexlerTemplateItems = useMemo<TemplateListItem[]>(
    () =>
      WEXLER_PROMPT_TEMPLATES.map((t) => ({
        id: t.id,
        label: t.label,
        icon: t.icon,
        body: t.template,
        source: 'wexler',
      })),
    [],
  );

  const organisationTemplateItems = useMemo<TemplateListItem[]>(
    () =>
      orgTemplates.map((t) => ({
        id: t.id,
        label: t.title,
        body: t.body,
        source: 'organisation',
        createdDate: t.createdDate,
      })),
    [orgTemplates],
  );

  const personalTemplateItems = useMemo<TemplateListItem[]>(
    () =>
      userTemplates
        .filter((t) => showOtherMatterTemplates || t.matterId === matterId)
        .map((t) => ({
          id: t.id,
          matterId: t.matterId ?? undefined,
          label: t.title,
          body: t.body,
          source: 'personal',
          createdDate: t.createdDate,
        })),
    [matterId, showOtherMatterTemplates, userTemplates],
  );

  const handleSelect = (template: TemplateListItem) => {
    trackCustomEvent(TrackingEventsV2.KimUseTemplate, {
      matterId,
      templateSource: template.source ?? 'personal',
      source: 'templates_popover',
    });
    onSelect(template.body);
  };

  const handleConfirmDelete = () => {
    if (!deletingTemplate) return;
    deleteTemplate(
      { id: deletingTemplate.id, templateMatterId: deletingTemplate.matterId },
      {
        onSuccess: () => setDeletingTemplate(null),
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Error deleting template. Please try again.');
        },
      },
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={onOpenChange}>
        {isControlled ? (
          anchor && <PopoverAnchor asChild>{anchor}</PopoverAnchor>
        ) : (
          <PopoverTrigger asChild>
            <Button variant={'ghost'} size={'sm'}>
              Templates
              <Icon className="lucide--chevron-down" />
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent
          side={side}
          align={align}
          className="-mt-1 -mr-2 w-[26rem] max-w-none p-0"
          onCloseAutoFocus={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-8 w-full justify-start gap-1 p-5 px-3">
              <TabsTrigger value="wexler" className="px-2 py-3 pb-2 text-[13px]">
                Wexler
              </TabsTrigger>
              <TabsTrigger value="organisation" className="px-2 py-3 pb-2 text-[13px]">
                {t('Organisation')}
              </TabsTrigger>
              <TabsTrigger value="personal" className="px-2 py-3 pb-2 text-[13px]">
                Personal
              </TabsTrigger>
            </TabsList>

            <TabsContent value="wexler" className="m-0 max-h-80 overflow-y-auto">
              <TemplateList
                templates={wexlerTemplateItems}
                emptyMessage="No Wexler templates"
                onSelect={handleSelect}
              />
            </TabsContent>

            <TabsContent value="organisation" className="m-0 max-h-80 overflow-y-auto">
              <SearchableTemplateList
                templates={organisationTemplateItems}
                emptyMessage={`No ${t('organisation')} templates`}
                onSelect={handleSelect}
              />
            </TabsContent>

            <TabsContent value="personal" className="m-0 max-h-80 overflow-y-auto">
              <SearchableTemplateList
                templates={personalTemplateItems}
                emptyMessage={
                  showOtherMatterTemplates ? 'No personal templates' : 'No personal templates in this Matter'
                }
                onSelect={handleSelect}
                onEdit={(t) => onEditTemplate({ id: t.id, matterId: t.matterId, title: t.label, body: t.body })}
                onDelete={(t) => setDeletingTemplate(t)}
              />

              <div className="m-1.75 mt-0 flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.75">
                <Checkbox
                  id="show-other-matter-templates"
                  checked={showOtherMatterTemplates}
                  onCheckedChange={(checked) => setShowOtherMatterTemplates(checked === true)}
                  className="scale-80 bg-white"
                />
                <label
                  htmlFor="show-other-matter-templates"
                  className="mt-px text-xs leading-none text-gray-400 select-none"
                >
                  Include templates from other Matters
                </label>
              </div>
            </TabsContent>
          </Tabs>

          {activeTab === 'organisation' ? (
            canManageOrgTemplates && (
              <div className="px-1.5">
                <hr />
                <Link
                  to="/organisation-settings/templates"
                  className="my-1.25 flex w-full items-center gap-2 rounded-sm px-2 py-1.25 text-left text-[13px] text-gray-800 duration-150 hover:bg-gray-50"
                >
                  <Icon className="lucide--settings-2" /> Manage templates
                </Link>
              </div>
            )
          ) : (
            <div className="px-1.5">
              <hr />
              <button
                onClick={onOpenTemplateBuilder}
                className="mt-1.25 flex w-full items-center gap-2 rounded-sm px-2 py-1.25 text-left text-[13px] text-gray-800 duration-150 hover:bg-gray-50"
              >
                <Icon className="lucide--plus" /> Build a new template
              </button>

              <div className="flex gap-2 px-2.25 pt-1.5 pb-3 text-xs text-gray-400">
                <Icon className="lucide--sparkle text-[11px]" />
                <p className="-mt-0.5">
                  You can also generate templates from existing answers using the{' '}
                  <Icon className="lucide--layout-template mx-0.25 -mb-0.5 inline-flex text-teal-500" /> button
                </p>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <ConfirmationModal
        isOpen={deletingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingTemplate(null);
        }}
        title="Delete template"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete <strong>{deletingTemplate?.label}</strong>? This cannot be undone.
      </ConfirmationModal>
    </>
  );
}
